import type { DashboardPromoBannerConfig } from 'src/components/Dashboards/dash-promo-types';
import type { VerivoxDismissSurface } from 'src/components/PromotionalIntegrations/Verivox/utils/verivoxDismissals';
import { dismissVerivoxSurface } from 'src/components/PromotionalIntegrations/Verivox/utils/verivoxDismissals';
import type { VerivoxBannerLayout } from 'src/components/PromotionalIntegrations/Verivox/banners/verivox-banner-layout';
import VerivoxBannerWidget from 'src/components/PromotionalIntegrations/Verivox/banners/VerivoxBannerWidget.svelte';
import VerivoxBannerInline from 'src/components/PromotionalIntegrations/Verivox/banners/VerivoxBannerInline.svelte';
import VerivoxOffersBannerWidget from 'src/components/PromotionalIntegrations/Verivox/banners/VerivoxOffersBannerWidget.svelte';
import VerivoxOffersBannerInline from 'src/components/PromotionalIntegrations/Verivox/banners/VerivoxOffersBannerInline.svelte';
import GenericPromoBanner from 'src/components/Dashboards/DashWidgets/Components/GenericPromoBanner.svelte';
import {
	isVerivoxEligibleForDashPromos,
	shouldShowVerivoxOffersBanner,
	shouldShowVerivoxPromoBanner,
} from 'src/helpers/verivox_promo_helpers';
import { modalStore } from 'src/stores/ui/modal_states';
import { VerivoxSetupModalConfig } from 'src/configs/modals/VerivoxSetupModalConfig';
import {
	dismissDashPromoBanner,
	getActiveDashPromoSamples,
	getDashPromoMaxBanners,
	isDashPromoBannerDismissed,
} from 'src/stores/features/dev/dashboard-promo-samples';
import { openExternalLink } from 'src/helpers/utilities';
import type { ComponentType } from 'svelte';
import { VERIVOX_CTA_OPEN_SETUP, VERIVOX_SETUP_BANNER_ID } from 'src/helpers/banner_views/banner-ids';
import { trackBannerCta } from 'src/helpers/banner_views/banner-views-helpers';

export type { DashboardPromoBannerConfig } from 'src/components/Dashboards/dash-promo-types';

/** Layouts used by dashboard strip / widget (not Notifications). */
export type DashPromoVerivoxLayout = Extract<VerivoxBannerLayout, 'widget' | 'inline'>;

const openVerivoxSetup = (surface: VerivoxDismissSurface) => {
	void trackBannerCta(VERIVOX_SETUP_BANNER_ID, VERIVOX_CTA_OPEN_SETUP, {
		scope: 'per-surface',
		surface,
	});
	modalStore.showModal(VerivoxSetupModalConfig(true));
};

const setupBannerComponent = (layout: DashPromoVerivoxLayout): ComponentType =>
	layout === 'inline' ? VerivoxBannerInline : VerivoxBannerWidget;

const offersBannerComponent = (layout: DashPromoVerivoxLayout): ComponentType =>
	layout === 'inline' ? VerivoxOffersBannerInline : VerivoxOffersBannerWidget;

const createVerivoxSetupBanner = (
	surface: VerivoxDismissSurface,
	layout: DashPromoVerivoxLayout,
): DashboardPromoBannerConfig => ({
	id: 'verivox-setup',
	kind: 'advanced',
	isVisible: () => shouldShowVerivoxPromoBanner(surface),
	dismissable: true,
	onDismiss: () => dismissVerivoxSurface(surface, 'setup'),
	component: setupBannerComponent(layout),
	props: {
		// Gallery / rail owns the X
		dismissable: false,
		dismissSurface: surface,
		onCtaClick: () => openVerivoxSetup(surface),
	},
});

const createVerivoxOffersBanner = (
	surface: VerivoxDismissSurface,
	layout: DashPromoVerivoxLayout,
): DashboardPromoBannerConfig => ({
	id: 'verivox-offers',
	kind: 'advanced',
	isVisible: () => shouldShowVerivoxOffersBanner(surface),
	dismissable: true,
	onDismiss: () => dismissVerivoxSurface(surface, 'offers'),
	component: offersBannerComponent(layout),
	props: {
		dismissable: false,
		dismissSurface: surface,
	},
});

const createGenericSampleBanners = (): DashboardPromoBannerConfig[] =>
	getActiveDashPromoSamples().map((sample) => ({
		id: sample.id,
		kind: 'generic' as const,
		isVisible: () => !isDashPromoBannerDismissed(sample.id),
		dismissable: true,
		onDismiss: () => dismissDashPromoBanner(sample.id),
		component: GenericPromoBanner,
		title: sample.title,
		message: sample.message,
		ctaLabel: sample.ctaLabel,
		ctaPosition: sample.ctaPosition ?? 'bottom-left',
		ctaHref: sample.ctaHref,
		background: sample.colorToken
			? { type: 'colorToken' as const, token: sample.colorToken }
			: sample.solidColor
				? { type: 'solid' as const, color: sample.solidColor }
				: { type: 'image' as const, src: sample.image },
		props: {
			title: sample.title,
			message: sample.message,
			ctaLabel: sample.ctaLabel,
			ctaPosition: sample.ctaPosition ?? 'bottom-left',
			ctaHref: sample.ctaHref,
			background: sample.colorToken
				? { type: 'colorToken', token: sample.colorToken }
				: sample.solidColor
					? { type: 'solid', color: sample.solidColor }
					: { type: 'image', src: sample.image },
			onCtaClick: sample.ctaHref ? () => openExternalLink(sample.ctaHref as string) : undefined,
			flush: true,
		},
	}));

/**
 * Builds the ordered, capped list of visible promo banners for a surface.
 * @param layout `widget` (~180px BannersWidget) or `inline` (~120px GlobalInlineBanners)
 *
 * Verivox is not listed by default — only when eligible via `de_users` or
 * Dev Modal force-enable. Generic sample promos are independent and stay
 * empty until enabled in Dev Tools.
 */
export const buildDashboardPromoBanners = (
	surface: VerivoxDismissSurface,
	layout: DashPromoVerivoxLayout = 'widget',
): DashboardPromoBannerConfig[] => {
	const pool: DashboardPromoBannerConfig[] = [...createGenericSampleBanners()];

	if (isVerivoxEligibleForDashPromos()) {
		pool.unshift(
			createVerivoxSetupBanner(surface, layout),
			createVerivoxOffersBanner(surface, layout),
		);
	}

	const max = getDashPromoMaxBanners();
	return pool.filter((banner) => banner.isVisible()).slice(0, max);
};

/** Sample/campaign slides (Dev Tools). Verivox uses `kind: 'advanced'`. */
export const isVerivoxDashPromoBanner = (banner: DashboardPromoBannerConfig): boolean =>
	banner.kind === 'advanced';

/**
 * Visible banners for a surface.
 * Generic samples only when the Dev slot is on; Verivox still appears for de_users.
 */
export const resolveVisibleDashPromoBanners = (
	surface: VerivoxDismissSurface,
	layout: DashPromoVerivoxLayout = 'widget',
	includeGenericSamples = false,
): DashboardPromoBannerConfig[] => {
	const banners = buildDashboardPromoBanners(surface, layout);
	if (includeGenericSamples) return banners;
	return banners.filter(isVerivoxDashPromoBanner);
};

/** @deprecated Use buildDashboardPromoBanners('energyDashboard') */
export const ENERGY_DASHBOARD_PROMO_BANNERS: DashboardPromoBannerConfig[] = [];

/** Compatibility export — prefer buildDashboardPromoBanners at call sites. */
export const getEnergyDashboardPromoBanners = (): DashboardPromoBannerConfig[] =>
	buildDashboardPromoBanners('energyDashboard', 'widget');
